/**
 * Text utilities shared by the index (search, table of contents) and the
 * renderer, so heading anchors computed from raw Markdown match the ids the
 * renderer puts on the page.
 */

/** GitHub-style heading slug: lowercase, punctuation dropped, spaces to hyphens. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, '')
    .replace(/ /g, '-')
}

/** Produces unique slugs in document order, like GitHub (`setup`, `setup-1`, ...). */
export class Slugger {
  private seen = new Map<string, number>()
  slug(text: string): string {
    const base = slugify(text) || 'section'
    let id = base
    while (this.seen.has(id)) {
      const n = this.seen.get(base)! + 1
      this.seen.set(base, n)
      id = `${base}-${n}`
    }
    this.seen.set(id, 0)
    return id
  }
}

/** Plain text of inline Markdown: code spans keep their content, markup is removed. */
export function inlineToText(md: string): string {
  return md
    .split(/(`+[^`]*`+)/)
    .map((part, i) => {
      if (i % 2 === 1) return part.replace(/^`+\s?|\s?`+$/g, '')
      return part
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/<[^>]+>/g, '')
        .replace(/(\*\*|__|~~)(.+?)\1/g, '$2')
        .replace(/(^|[^\w*])\*(?!\s)(.+?)\*(?!\w)/g, '$1$2')
        .replace(/(^|\W)_(?!\s)(.+?)_(?!\w)/g, '$1$2')
        .replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, '$1')
    })
    .join('')
    .trim()
}

export interface Heading {
  depth: number
  text: string
  id: string
}

const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/
const ATX = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/

/** Calls `fn` for every line that is not inside a fenced code block. */
function forEachProseLine(
  body: string,
  fn: (line: string) => void,
  onCode?: (line: string) => void,
) {
  let fence: string | null = null
  for (const line of body.split(/\r?\n/)) {
    if (fence) {
      if (new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}\\s*$`).test(line))
        fence = null
      else onCode?.(line)
      continue
    }
    const open = FENCE_OPEN.exec(line)
    if (open) {
      fence = open[1]!
      continue
    }
    fn(line)
  }
}

/** All ATX headings (## through ######) with the ids the renderer assigns. */
export function extractHeadings(body: string): Heading[] {
  const slugger = new Slugger()
  const out: Heading[] = []
  forEachProseLine(body, (line) => {
    const m = ATX.exec(line)
    if (!m) return
    const depth = m[1]!.length
    const text = inlineToText(m[2]!)
    // Level-1 headings are not used in articles (the title comes from frontmatter),
    // but they still take a slug so ids stay in step with the renderer.
    out.push({ depth, text, id: slugger.slug(text) })
  })
  return out
}

function stripBlockMarkup(line: string): string {
  return line
    .replace(/^ {0,3}#{1,6}\s+/, '')
    .replace(/^\s*>\s?/, '')
    .replace(/^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i, '')
    .replace(/^\s*(?:[-*+]|\d+\.)\s+(?:\[[ xX]\]\s+)?/, '')
    .replace(/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/, '')
    .replace(/\s*\|\s*/g, ' ')
    .trim()
}

/**
 * Markdown body as plain text, one paragraph per line. Lines of a wrapped
 * paragraph are joined first so emphasis split across lines is removed.
 * With `code: false`, fenced code is left out (for search snippets).
 */
export function markdownToPlainText(body: string, { code = true } = {}): string {
  const out: string[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) out.push(inlineToText(para.join(' ')))
    para = []
  }
  forEachProseLine(
    body,
    (line) => {
      const blockStart = /^\s*(?:#{1,6}\s|>|[-*+]\s|\d+\.\s|\|)/.test(line)
      const t = stripBlockMarkup(line)
      if (!t) return flush()
      if (blockStart) flush()
      para.push(t)
    },
    (codeLine) => {
      flush()
      if (code) out.push(codeLine)
    },
  )
  flush()
  return out.filter((l) => l.trim() !== '').join('\n')
}

/** Every link target in the Markdown body (inline links only), outside code. */
export function extractLinks(body: string): string[] {
  const lines: string[] = []
  // Links can wrap across lines, so match against the whole prose text.
  forEachProseLine(
    body,
    (line) => lines.push(line),
    () => lines.push(''),
  )
  const prose = lines.join('\n').replace(/(`+)[\s\S]*?\1/g, '')
  return [...prose.matchAll(/!?\[[^\]]*\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/g)].map(
    (m) => m[1]!,
  )
}
